import { useCallback, useEffect, useState } from "react";

import { ApiError, api } from "@/lib/api";
import type { EstadoBuzon } from "@/lib/api";
import { fmtBytes } from "@/lib/formato";
import Toggle from "@/ui/Toggle";
import { useToast } from "@/ui/Toast";

/**
 * «Dejar que ArkTickets revise mi buzón», pegado al correo empresarial.
 *
 * Vive en Perfil y no en Configuración porque es **por persona**: usa TU correo
 * y TU contraseña, y solo tú ves tu buzón. Configuración pone las reglas
 * comunes (cada cuánto, en qué horario); aquí van las llaves. Es el mismo
 * reparto que ya existe para el SMTP.
 *
 * La contraseña no se pide otra vez: es el mismo buzón que ya está arriba.
 * Guardar el mismo secreto dos veces solo agrega un lugar donde se quede viejo.
 */
export default function BuzonPanel() {
  const toast = useToast();
  const [estado, setEstado] = useState<EstadoBuzon | null>(null);
  const [cargando, setCargando] = useState(true);
  const [probando, setProbando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      setEstado(await api.get<EstadoBuzon>("/api/buzon/estado"));
    } catch {
      setEstado(null);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function alternar(v: boolean) {
    // Pintado optimista: un switch que tarda medio segundo en moverse se
    // siente roto y la gente le da dos veces.
    setEstado((e) => (e ? { ...e, habilitado: v } : e));
    try {
      const r = await api.post<EstadoBuzon>("/api/buzon/activar", { habilitado: v });
      setEstado(r);
      toast.success(v ? "Lectura del buzón activada" : "Lectura del buzón desactivada", "");
    } catch (e) {
      setEstado((prev) => (prev ? { ...prev, habilitado: !v } : prev));
      const msg = e instanceof ApiError ? e.detail : "Error";
      toast.error("No se pudo guardar", msg);
    }
  }

  async function probar() {
    setProbando(true);
    try {
      const r = await api.post<{ ok: boolean; detalle?: string }>("/api/buzon/probar");
      if (r.ok) toast.success("Conectado", "Pudimos entrar a tu buzón sin tocar nada.");
      else toast.error("No pudimos entrar", r.detalle ?? "");
      void cargar();
    } catch (e) {
      const msg = e instanceof ApiError ? e.detail : "Error";
      toast.error("No pudimos entrar", msg);
    } finally {
      setProbando(false);
    }
  }

  if (cargando || !estado) return null;

  const cuota =
    estado.cuota_usada_kb && estado.cuota_total_kb
      ? Math.round((100 * estado.cuota_usada_kb) / estado.cuota_total_kb)
      : null;

  return (
    <div className="card space-y-3">
      <div>
        <h2 className="text-base font-semibold">Revisar mi buzón</h2>
        <p className="mt-0.5 text-xs text-zinc-400">
          ArkTickets se asoma a los títulos de tu correo para avisarte qué puede servir y para
          meter solas las respuestas de casos que ya existen.{" "}
          <strong className="text-zinc-300">
            Nunca marca como leído, ni mueve, ni borra nada.
          </strong>
        </p>
      </div>

      {!estado.configurado ? (
        <p className="rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
          Primero configura tu correo empresarial aquí arriba.
        </p>
      ) : (
        <>
          <Toggle
            checked={estado.habilitado}
            onChange={(v) => void alternar(v)}
            label="Dejar que ArkTickets revise mi buzón"
            hint="Usa la misma contraseña de tu correo empresarial de arriba; no hay que capturar otra."
          />

          {estado.habilitado && (
            <div className="flex flex-wrap items-center gap-3 text-xs">
              {estado.ultimo_error ? (
                <span className="text-red-300">
                  Se desconectó: {estado.ultimo_error}
                  {estado.ultimo_error.toLowerCase().includes("contraseña") && (
                    <> Ojo: es la misma que usas para enviar correos.</>
                  )}
                </span>
              ) : estado.conectado ? (
                <span className="text-emerald-300">✓ Conectado</span>
              ) : (
                <span className="text-zinc-400">Sin revisar todavía</span>
              )}
              {estado.ultima_revision && (
                <span className="text-zinc-500">
                  Última revisión: {new Date(estado.ultima_revision).toLocaleString("es-MX")}
                </span>
              )}
              {/* Dato pasivo, sin aviso: se muestra porque ya se está midiendo,
                  pero vigilarlo automáticamente se descartó a conciencia. */}
              {cuota != null && (
                <span className={cuota >= 85 ? "text-amber-300" : "text-zinc-500"}>
                  Buzón al {cuota}% ({fmtBytes((estado.cuota_usada_kb ?? 0) * 1024)} de{" "}
                  {fmtBytes((estado.cuota_total_kb ?? 0) * 1024)})
                </span>
              )}
              <button
                type="button"
                onClick={() => void probar()}
                disabled={probando}
                className="btn-ghost text-xs disabled:opacity-50"
              >
                {probando ? "Probando…" : "Probar"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
